import React, { FunctionComponent, useCallback, useContext } from 'react';
import { PlayerBase } from '@24i/player-base';
import { BLOCKING_REASON_TYPES } from '@24i/nxg-sdk-photon';
import { isMobile } from 'react-device-detect';
import { useDebouncedCallback } from 'use-debounce';
import { PlayerUIContext, PlayerUIContextProvider } from '../Context';
import { Container } from './styled';

// eslint-disable-next-line no-magic-numbers
const CLICK_DELAY = isMobile ? 300 : 0;
const PERCENT_OFFSET_FROM_BORDER_TO_SEEK = 20;
const FULL_PERCENT = 100;
const SEEK_AMOUNT = 10000;

interface IPlayerUIContainer {
    playerInstance: PlayerBase;
    blockingReason?: BLOCKING_REASON_TYPES;
    className?: string;
    style?: React.CSSProperties;
}

export const WithoutContextPlayerUIContainer: FunctionComponent<IPlayerUIContainer> = ({
    playerInstance,
    blockingReason,
    children,
    className,
    style,
}) => {
    const {
        state: { isDragSeeking },
    } = useContext(PlayerUIContext);

    const [onClickHandlerDebounced, cancelClickHandler] = useDebouncedCallback(() => {
        if (!playerInstance || blockingReason) {
            return;
        }
        const isPaused = playerInstance.paused;
        if (isPaused) playerInstance.play();
        if (!isPaused) playerInstance.pause();
    }, CLICK_DELAY);

    const onClickHandler = useCallback(() => {
        /*
         * Do not perform any actions if dragging gesture is in progress.
         * For example during drag and drop seeking.
         */
        if (isDragSeeking) return;
        onClickHandlerDebounced();
    }, [playerInstance, isDragSeeking]);

    const onDoubleClickHandler = useCallback(
        (event) => {
            if (isMobile) {
                cancelClickHandler();
                /*
                 * Do not perform any actions if dragging gesture is in progress.
                 * For example during drag and drop seeking.
                 */
                if (isDragSeeking) return;
                if (!playerInstance) return;
                if (
                    (event.nativeEvent.offsetX / event.target.offsetWidth) * FULL_PERCENT <
                    PERCENT_OFFSET_FROM_BORDER_TO_SEEK
                ) {
                    playerInstance.backward(SEEK_AMOUNT);
                } else if (
                    (event.nativeEvent.offsetX / event.target.offsetWidth) * FULL_PERCENT >
                    FULL_PERCENT - PERCENT_OFFSET_FROM_BORDER_TO_SEEK
                ) {
                    playerInstance.forward(SEEK_AMOUNT);
                }
            } else {
                if (playerInstance) {
                    // eslint-disable-next-line no-param-reassign
                    playerInstance.fullscreen = !playerInstance.fullscreen;
                }
                if (event) event.stopPropagation();
            }
        },
        [playerInstance, isDragSeeking]
    );

    return (
        <Container
            className={`PlayerUIContainer ${className}`}
            data-test="PlayerUIContainer-Container"
            id="PlayerUIContainer-Container"
            onClick={onClickHandler}
            onDoubleClick={onDoubleClickHandler}
            {...{ style }}
        >
            {children}
        </Container>
    );
};

WithoutContextPlayerUIContainer.displayName = 'WithoutContextPlayerUIContainer';

export const PlayerUIContainer: FunctionComponent<IPlayerUIContainer> = (props) => (
    <PlayerUIContextProvider>
        <WithoutContextPlayerUIContainer {...props} />
    </PlayerUIContextProvider>
);

PlayerUIContainer.displayName = 'PlayerUIContainer';

PlayerUIContainer.defaultProps = {
    className: '',
    style: null,
};
